import { FC, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SidebarContext } from "../../Sidebar/SidebarContext";
import { useAppSelector } from "../../../../utilities/hooks/redux.hooks";
import { SidebarMenuExtended } from "./domain/sidebarNav";
import useSession from "../../auth/services/useSession";
import { MENU_PERMISSION_BASE } from "../../menu/menu.constants";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LogoutIcon from '@mui/icons-material/Logout';
import { appConfig } from "../../../../config";
import { decodeToken } from "../../auth/utilities/jwt";
import Profile from "./components/Profile";
import Breadcrumb from "../../Breadcrumb/Breadcrumb";
import SidebarNav from "./components/SidebarNav";
import { useLogoutMutation } from "../../auth/services/authApi";
import { SidebarNavLogout } from "./styled-components/SidebarNavLogout";
import translate from "../../../../language/translate";

interface TopbarLayoutProps {
  children?: ReactNode;
}

const TopbarLayout: FC<TopbarLayoutProps> = ({ children }) => {
  const { hasPermission } = useSession();
  const { pathname } = useLocation();
  const data = useAppSelector((state) => state.menuStatus.menus);
  const navigate = useNavigate();
  const intl = useIntl();
  const pathTraducir = pathname.split('/')[1] === '' ? 'home' : pathname.split('/')[1];
  const [logout] = useLogoutMutation();
  const session = useAppSelector((state) => state.session);
  const user =
    session.token !== undefined ? decodeToken(session.token) : undefined;
  const HOME_URLS = ["/", "/home", "/dashboard", `/profile/${user.id}`];
  let { verMenuLateral, mostrarOcultarMenu } = useContext(SidebarContext);
  const crumb =
    pathname === "/"
      ? [{ label: "home", link: "/" }]
      : pathname
          .replace(/[?#].*$/, "")
          .split("/")
          .filter((segment) => segment !== "")
          .map((segment, index, array) => ({
            label: segment,
            link: "/" + array.slice(0, index + 1).join("/"),
          }));

  const menus: SidebarMenuExtended[] = data?.filter((menu) =>
    hasPermission(`${MENU_PERMISSION_BASE}${menu.action}`)
  );

  function eraseDepthSubmenus(
    menu: SidebarMenuExtended[]
  ): SidebarMenuExtended[] {
    const result = [];
    for (let i = 0; i < menu.length; i++) {
      const hasPerm = hasPermission(`${MENU_PERMISSION_BASE}${menu[i].action}`);
      const processedItem = {
        ...menu[i],
        submenus: hasPerm ? eraseDepthSubmenus(menu[i].submenus) : [],
      };
      if (hasPerm || processedItem.submenus.length > 0) {
        result.push(processedItem);
      }
    }
    return result;
  }

  const goToHome = () => {
    navigate(appConfig.HOME_PAGE);
  };

  let navRef: any = useRef(null);
  const [navControl, setNavControl] = useState<{width: number, visible: boolean}>({width: 0, visible: false});
  const divRef: any = useRef(null);
  const divRefDos: any = useRef(null);
  const [anchoDiv, setAnchoDiv] = useState(0);
  const [anchoChico, setAnchoChico] = useState(0);

  useEffect(() => {
    const anchoPantalla = window.innerWidth;
    setAnchoChico(anchoChico - divRef.current.offsetWidth);
    setAnchoDiv((divRef.current.offsetWidth * 99) / anchoPantalla);
    setNavControl({width: navRef.current.offsetWidth, visible: false});
    setTimeout(() => {
      const anchoPantalla = window.innerWidth;
      setAnchoDiv((divRef.current.offsetWidth * 99) / anchoPantalla);
      if (navRef.current) {
        setNavControl({width: navRef.current.offsetWidth, visible: true});
      }
    }, 350);
  }, [verMenuLateral, divRef.current, window.innerWidth]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.reload();
  };  

  return (
    <ContenedorPrincipal menuLateral={verMenuLateral} ref={divRefDos}>
      <Menu ref={divRef}>
        <Logo>
          {verMenuLateral ? (
            <img
              className="logoChico"
              src="/assets/logo_tecbi_small.png"
              alt="TecbiLogo"
            />
          ) : (
            <img
              className="logoGrande"
              src="/assets/logo_tecbi.png"
              alt="TecbiLogo"
            />
          )}
        </Logo>
        <Nav ref={navRef}>
          <div className="contenedor-menu-sidebar">
            <ContenedorIconos navWidth={navControl.width}>
              {verMenuLateral ? (
                <ArrowForwardIcon onClick={mostrarOcultarMenu} />
              ) : (
                <ArrowBackIcon onClick={mostrarOcultarMenu} />
              )}
            </ContenedorIconos>
            <SidebarNav
              data={eraseDepthSubmenus(menus)}
              isSuccess={data.length > 0}
              openNav={false}
              initialPath={ intl.formatMessage({ id: pathTraducir})}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"}}>
            <ContenedorUsuario verMenu={verMenuLateral}>
              <Profile />
            </ContenedorUsuario>

              {!verMenuLateral ? 
                <SidebarNavLogout onClick={handleLogout}>
                  <LogoutIcon />
                  <span>{translate("exit")}</span>
                </SidebarNavLogout>
              :null}
          </div>
        </Nav>
        <Isologo>
          <div className="isologo-cashbn">
            {verMenuLateral ? (
              <img
                className="logoChico"
                src="/assets/iso-reverso-blanco.png"
                alt="Tecbi-Isologo"
              />
            ) : (
              <img
                className="logoGrande"
                src="/assets/CashLogoBN.png"
                alt="Tecbi-Isologo"
              />
            )}
          </div>
        </Isologo>
      </Menu>

      <Contenido>
        <Main anchoDiv={anchoDiv}>
          <TopbarContent>
            {data &&
            data.length > 0 &&
            !HOME_URLS.includes(crumb[0].link) &&
            data?.find(
              (menu: SidebarMenuExtended) => crumb[0].link === menu?.action
            )?.name ? (
              <TituloAndBreadcrumb>
                <TituloModulo>
                  <FormattedMessage
                    id={
                      data?.find(
                        (menu: SidebarMenuExtended) =>
                          crumb[0].link === menu?.action
                      )?.name
                    }
                  />
                </TituloModulo>
                <Breadcrumb breadcrumbList={crumb} />
              </TituloAndBreadcrumb>
            ) : null}
            {children || <Outlet />}
          </TopbarContent>
        </Main>
      </Contenido>
    </ContenedorPrincipal>
  );
};

export default TopbarLayout;

const Contenido = styled.div<any>`
  width: 100%;
`;

const ContenedorIconos = styled.div<any>`
  cursor: pointer;
  color: #fff;
  display: flex;
  transition: all 0s ease-in-out;
  gap: 10px;
  position: absolute;
  top: 29px;
  left: ${props => `${props.navWidth - 15}px`};
  svg {
    font-size: 30px;
    padding: 5px;
    background-color: #64b33b;
    border-radius: 100%;
    z-index: 5;
  }
`;

const ContenedorUsuario = styled.div<any>`
  /* background-color: #e4e5ec; */
  border-radius: 2%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: auto;
  width: 100%;
  min-height: 130px;
  z-index: 10;
  border-bottom: solid 1px rgba(0, 0, 0, 0.1);
  background-color: ${(props) => (props.verMenu ? "#706f6f" : "#f2f3fa")};
  opacity: ${(props) => (props.verMenu ? 0 : 1)};
  overflow: hidden;
  > * {
    opacity: ${(props) => (props.verMenu ? 0 : 1)};
    display: ${(props) => (props.verMenu ? 'none' : 'flex')};
  }
`;

const Main = styled.div<any>`
  width: ${(props) =>
    props.anchoDiv ? `${100 - props.anchoDiv}vw` : `${100 - props.anchoDiv}vw`};
  height: 100vh;
  background-color: #f2f3fa;
  overflow-y: hidden !important;
`;

const ContenedorPrincipal = styled.div<any>`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #706f6f;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  .logoGrande {
    object-fit: contain;
    width: 200px;
    height: 40px;
    margin: 15px 0;
  }
  .logoChico {
    object-fit: contain;
    width: 60px;
    height: 40px;
    margin: 15px 0;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  background-color: #706f6f;
  height: 100vh;
  padding: 8%;
  overflow-y: auto;
  ul {
    padding: 0 !important;
  }
  &::-webkit-scrollbar {
    display: none;
  }
  .contenedor-menu-sidebar {
    h5 {
      padding: 10px 0;
      color: #9a989e;
    }
    display: flex;
    justify-content: center;
    flex-direction: column;
  }
`;

const Isologo = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 5px;
  background-color: #706f6f;
  padding-bottom: 20px;
  img {
    object-fit: contain;
  }
  .logoGrande {
    width: 120px;
    height: 40px;
  }
  .logoChico {
    width: 30px;
    height: 40px;
  }
`;

const TopbarContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background-color: #fff;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: #fff;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #fff;
    color: #f2f3fa97;
  }
`;

const TituloModulo = styled.h1`
  width: 90%;
  text-transform: uppercase;
  color: #706f6f;
  padding: 10px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  @media (min-width: 1024px) {
    font-size: 20px;
    height: 100%;
  }
`;

const TituloAndBreadcrumb = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(0, 0, 0, 0.3);
  background-color: #fff;
`;
